import { Box, Button, Checkbox, Dialog, DialogActions, FormControlLabel, Paper, Stack, Typography } from "@mui/material";
import { merchantTableColumns, mearchatSkuDummyData } from "./data";
import { useEffect, useRef, useState } from "react";
import { converAttrToDatagridCol, createdByColumns, selectOptionsKeyName, statusOptions } from "data/globalData";
import { AuthState } from "context/AuthContext";
import { GlobalState } from "context/GlobalContext";
import { parseErrorMessage } from "utils/ErrorFunctions/parseErrorMessage";
import axios from "axios";
import { Camera, CameraAltRounded, FileDownload } from "@mui/icons-material";
import DatagridWithApiPagination from "components/ReusableComponents/DatagridWithApiPagination/DatagridWithApiPagination";
import SkuSectionTanstack from "../CreateShoot/SkuSection/SkuSectionTanstack";
import {
	sampleStatusColumn,
	shootStudioSampleDetailsColumns,
	shootSkuIcodeColumn,
	shootStudioSampleGrnImgUrlCol,
	shootStudioSampleImgUrlCol,
} from "../ShootsDashboard/data";
import { set } from "lodash";
import RenderInputField from "components/PimDashboard/CommonComponents/RenderInputField/RenderInputField";
import { grey } from "@mui/material/colors";
import SampleHistoryModal from "../MerchantData/SampleHistoryModal/SampleHistoryModal";
import CameraCapture from "./CameraCapture/CameraCapture";
import { createUpdatedDataPayload } from "utils/createUpdatedDataPayload";
import { custom } from "data/authData";

const allowedStudioReceivedStatuses = new Set([statusOptions.READY.key]);
// A bulk GRN image is only meaningful once the sample is physically in.
const allowedGrnUploadStatuses = new Set([statusOptions.RECEIVED.key]);
const blockedStudioEditStatuses = new Set([statusOptions.REJECTED.key]);
const inwardVisibleStatuses = [statusOptions.READY.key, statusOptions.RECEIVED.key, statusOptions.REJECTED.key];
const formatMySqlDateTime = (date = new Date()) => {
	const pad = (value) => String(value).padStart(2, "0");
	return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(
		date.getMinutes(),
	)}:${pad(date.getSeconds())}`;
};

const StudioSampleInward = () => {
	const { user } = AuthState();
	const { setLoadingOpen, setSnackBarOpen, setSnackBar, snackBar } = GlobalState();

	const disableEdit = user?.role?.[custom.SAMPLE_INWARD_VIEWER.key] === true;

	const [selectedSkus, setSelectedSkus] = useState([]);
	const [allSkus, setAllSkus] = useState([]);
	const [editModal, setEditModal] = useState(false);
	const [historyModal, setHistoryModal] = useState(false);
	const [originalData, setOriginalData] = useState(null);
	const [editedData, setEditedData] = useState(null);
	const [selectedHistorySku, setSelectedHistorySku] = useState(null);
	const [image, setImage] = useState(null);
	const [bulkEdit, setBulkEdit] = useState(false);
	const [cameraActive, setCameraActive] = useState(null);
	const [markAsReceived, setMarkAsReceived] = useState(false);
	const [markAsRejected, setMarkAsRejected] = useState(false);
	const [grnModal, setGrnModal] = useState(false);
	const [grnImage, setGrnImage] = useState(null);

	useEffect(() => {
		if (selectedSkus.length > 1) setBulkEdit(true);
		else setBulkEdit(false);
	}, [selectedSkus]);

	const selectedSkuRows = allSkus.filter((item) => selectedSkus.includes(item[shootSkuIcodeColumn.key]));
	const isBulkReceivedEligible =
		selectedSkus.length > 0 &&
		selectedSkuRows.length === selectedSkus.length &&
		selectedSkuRows.every((item) => allowedStudioReceivedStatuses.has(item?.[sampleStatusColumn.key]));
	const isSingleReceivedEligible = allowedStudioReceivedStatuses.has(editedData?.[sampleStatusColumn.key]);
	const canShowMarkAsReceived = bulkEdit ? isBulkReceivedEligible : isSingleReceivedEligible;
	const canShowRejectSample = canShowMarkAsReceived;
	const isGrnUploadEligible =
		selectedSkus.length > 0 &&
		selectedSkuRows.length === selectedSkus.length &&
		selectedSkuRows.every((item) => allowedGrnUploadStatuses.has(item?.[sampleStatusColumn.key]));

	const handleEditModalClose = () => {
		setEditModal(false);
		setEditedData(null);
		setOriginalData(null);
		setSelectedSkus([]);
		setImage(null);
		setMarkAsReceived(false);
		setMarkAsRejected(false);
	};

	const handleIcodeSelect = (data) => {
		if (blockedStudioEditStatuses.has(data?.[sampleStatusColumn.key])) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Rejected samples cannot be edited",
				severity: "warning",
			});
			return;
		}

		setEditedData(data);
		setOriginalData(data);
		setMarkAsReceived(false);
		setMarkAsRejected(false);
		setEditModal(true);
	};

	const handleStudioSelectionChange = (selectedIds) => {
		const nextSelectedIds = selectedIds.filter((id) => {
			const row = allSkus.find((item) => item[shootSkuIcodeColumn.key] === id);
			return !blockedStudioEditStatuses.has(row?.[sampleStatusColumn.key]);
		});

		if (nextSelectedIds.length !== selectedIds.length) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Rejected samples cannot be selected for bulk edit",
				severity: "warning",
			});
		}

		setSelectedSkus(nextSelectedIds);
	};

	const handleBulkEditButtonClick = () => {
		if (selectedSkus.length === 0) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Please select at least one SKU to edit",
				severity: "warning",
			});
			return;
		}
		setEditedData({});
		setOriginalData({});
		setImage(null);
		setMarkAsReceived(isBulkReceivedEligible);
		setMarkAsRejected(false);
		setEditModal(true);
	};
	const handleGrnButtonClick = () => {
		if (!isGrnUploadEligible) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: selectedSkus.length
					? "A bulk GRN image can only be uploaded for samples in RECEIVED status"
					: "Please select at least one received SKU",
				severity: "warning",
			});
			return;
		}
		setGrnImage(null);
		setGrnModal(true);
	};

	const handleGrnModalClose = () => {
		setGrnModal(false);
		setGrnImage(null);
	};

	const handleGrnUpload = async () => {
		if (!grnImage) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Please capture or choose a GRN image",
				severity: "warning",
			});
			return;
		}

		setLoadingOpen(true);
		try {
			const formData = new FormData();
			formData.append("grnImages", grnImage);

			const uploadRes = await axios.post("/api/studio/upload_sample_grn_images", formData, {
				headers: { "Content-Type": "multipart/form-data" },
			});
			const grnImgUrl = uploadRes.data?.result?.[0]?.publicUrl;
			if (!grnImgUrl) throw new Error("GRN image upload failed");

			const res = await axios.put("/api/studio/bulk_upload_sample_grn", {
				icode_list: selectedSkus,
				grn_img_url: grnImgUrl,
			});

			setAllSkus((prev) =>
				prev.map((item) =>
					selectedSkus.includes(item[shootSkuIcodeColumn.key])
						? { ...item, [shootStudioSampleGrnImgUrlCol.key]: grnImgUrl }
						: item,
				),
			);

			const mailFailedFor = res.data?.result?.mail_failed_for ?? [];
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: mailFailedFor.length
					? `GRN image saved, but the notification failed for: ${mailFailedFor.join(", ")}`
					: res.data?.message || "GRN image uploaded and merchants notified",
				severity: mailFailedFor.length ? "warning" : "success",
			});

			setSelectedSkus([]);
			handleGrnModalClose();
		} catch (err) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				severity: "error",
				message: parseErrorMessage(err),
			});
		} finally {
			setLoadingOpen(false);
		}
	};

	const handleDataSave = async (data) => {
		setLoadingOpen(true);
		let imagesArray = image ? [image] : [];
		try {
			if (markAsReceived && !canShowMarkAsReceived) {
				setSnackBarOpen(true);
				setSnackBar({
					...snackBar,
					open: true,
					message: "Only ready samples can be marked as received",
					severity: "error",
				});
				setLoadingOpen(false);
				return;
			}
			if (markAsRejected && !canShowRejectSample) {
				setSnackBarOpen(true);
				setSnackBar({
					...snackBar,
					open: true,
					message: "Only ready samples can be rejected",
					severity: "error",
				});
				setLoadingOpen(false);
				return;
			}

			const workingData = { ...(data || {}) };

			if (markAsReceived) {
				workingData[sampleStatusColumn.key] = statusOptions.RECEIVED.key;
				workingData[shootStudioSampleDetailsColumns.sample_inward_date.key] = formatMySqlDateTime();
			} else if (markAsRejected) {
				workingData[sampleStatusColumn.key] = statusOptions.REJECTED.key;
			}

			let payload = bulkEdit
				? createUpdatedDataPayload(
						workingData,
						{},
						{
							...shootStudioSampleDetailsColumns,
							[sampleStatusColumn.key]: sampleStatusColumn,
						},
					)
				: createUpdatedDataPayload(workingData, originalData, {
						...shootStudioSampleDetailsColumns,
						[sampleStatusColumn.key]: sampleStatusColumn,
					});

			if (imagesArray.length > 0) {
				const formData = new FormData();
				imagesArray.forEach((file) => {
					formData.append("sampleImages", file); // append each file
				});

				// Wait for upload to finish
				const res = await axios.post("/api/studio/upload_sample_images", formData, {
					headers: { "Content-Type": "multipart/form-data" },
				});

				if (!res.data?.result?.length) throw new Error("Image upload failed");

				// Set first image URL in payload (or handle multiple URLs if needed)
				payload[shootStudioSampleImgUrlCol.key] = res.data.result[0].publicUrl;
				workingData[shootStudioSampleImgUrlCol.key] = res.data.result[0].publicUrl;
			}
			if (Object.keys(payload).length === 0) {
				setSnackBarOpen(true);
				setSnackBar({
					...snackBar,
					message: "No changes made",
					severity: "warning",
				});
				setLoadingOpen(false);
				return;
			}
			if (bulkEdit) {
				await axios.put("/api/studio/bulk_update_shoot_studio_sample_details", {
					body: payload,
					icode_list: selectedSkus,
				});

				// Update local state for bulk
				setAllSkus((prev) =>
					prev.map((item) => (selectedSkus.includes(item[shootSkuIcodeColumn.key]) ? { ...item, ...payload } : item)),
				);
			} else {
				await axios.put(`/api/studio/update_shoot_studio_sample_details/${data[shootSkuIcodeColumn.key]}`, payload);

				// Update local state for single
				setAllSkus((prev) =>
					prev.map((item) =>
						item[shootSkuIcodeColumn.key] === data[shootSkuIcodeColumn.key] ? { ...item, ...payload } : item,
					),
				);
			}
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Data updated successfully",
				severity: "success",
			});

			setSelectedSkus([]);
			setCameraActive(false);
			handleEditModalClose();
		} catch (err) {
			setSnackBarOpen(true);

			setSnackBar({
				...snackBar,
				severity: "error",
				message: parseErrorMessage(err),
			});
		} finally {
			setLoadingOpen(false);
		}
	};

	const handleHistoryClick = (selectedRow) => {
		setSelectedHistorySku(selectedRow);
		setHistoryModal(true);
	};

	return (
		<Box display="flex" width="90vw" flexWrap="wrap" margin="auto">
			<Box pt={1} width="100%">
				<SkuSectionTanstack
					selectedSkus={selectedSkus}
					setSelectedSkus={setSelectedSkus}
					processCheckboxSelectionChange={handleStudioSelectionChange}
					shootData={{}}
					defaultFilterObj={{ [sampleStatusColumn.key]: inwardVisibleStatuses }}
					onRowClick={handleIcodeSelect}
					minHeight={"72vh"}
					maxHeight={"72vh"}
					skusState={allSkus}
					setSkusState={setAllSkus}
					hideSearchHeader={true}
					showActionColumn={true}
					actionList={[{ name: "History", onClick: handleHistoryClick }]}
					disableSelection={disableEdit}
					enableExcelExport={true}
					exportFileName="sample_sku_details.xlsx"
					childrenAboveTable={
						<Box flex={1} alignItems="flex-end" display="flex" gap={1}>
							<Button variant="contained" disabled={!bulkEdit} onClick={handleBulkEditButtonClick} size="small">
								Bulk Edit
							</Button>
							<Button
								variant="outlined"
								disabled={disableEdit || !isGrnUploadEligible}
								onClick={handleGrnButtonClick}
								size="small"
								startIcon={<CameraAltRounded fontSize="small" />}>
								Upload GRN
							</Button>
						</Box>
					}
				/>
			</Box>

			<Dialog fullWidth maxWidth="lg" open={editModal} onClose={handleEditModalClose}>
				<Typography variant="h6" p={2} pb={1}>
					{bulkEdit ? "Bulk" : ""}Edit Merchant Sample Details
				</Typography>

				<Box sx={{ p: 2, pt: 1, borderBottom: "1px solid #ccc" }}>
					{bulkEdit ? (
						<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
							<Typography variant="h6" fontSize={16} sx={{ width: "100%" }} color={grey[700]}>
								Selected Icodes
							</Typography>
							{selectedSkus.map((sku) => (
								<Box
									key={sku}
									sx={{
										backgroundColor: grey[400],
										borderRadius: "5px",
										padding: "5px",
										display: "flex",
										alignItems: "center",
										gap: 1,
									}}>
									<Typography variant="subtitle1">{sku}</Typography>
								</Box>
							))}
						</Box>
					) : (
						<Typography variant="subtitle1">SKU ICODE: {editedData?.[shootSkuIcodeColumn.key]}</Typography>
					)}
				</Box>

				<Box sx={{ p: 2, display: "flex", flexWrap: "wrap", gap: 2 }}>
					{Object.values(shootStudioSampleDetailsColumns).map((column) => {
						return (
							<RenderInputField
								key={column.key}
								column={column}
								data={editedData}
								setData={setEditedData}
								selectOptObjName={selectOptionsKeyName}
								width="32%"
								longWidth="66%"
							/>
						);
					})}
				</Box>

				<Box sx={{ px: 2, pb: 1 }}>
					<FormControlLabel
						control={
							<Checkbox
								checked={markAsReceived}
								onChange={(e) => {
									const checked = e.target.checked;
									setMarkAsReceived(checked);
									if (checked) setMarkAsRejected(false);
								}}
								disabled={disableEdit || !canShowMarkAsReceived}
							/>
						}
						label="Mark as received"
					/>
					<FormControlLabel
						control={
							<Checkbox
								checked={markAsRejected}
								onChange={(e) => {
									const checked = e.target.checked;
									setMarkAsRejected(checked);
									if (checked) setMarkAsReceived(false);
								}}
								disabled={disableEdit || !canShowRejectSample}
							/>
						}
						label="Reject sample"
					/>
					{!canShowMarkAsReceived && (
						<Typography variant="caption" color="text.secondary">
							{bulkEdit
								? "Bulk receive and reject actions are enabled only when all selected SKUs are in READY status."
								: "Receive and reject actions are enabled only when status is READY."}
						</Typography>
					)}
				</Box>

				{/* IMAGE CAPTURE SECTION */}
				<Stack sx={{ p: 2, pt: 0 }} spacing={2}>
					<Typography variant="h6" fontSize={18} color={grey[700]}>
						Sample Image (Optional)
					</Typography>

					<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, width: "70%" }}>
						{editedData?.[shootStudioSampleImgUrlCol.key] && !cameraActive ? (
							<Box display="flex" columnGap={2}>
								<Paper
									elevation={3}
									sx={{
										width: 500,
										borderRadius: 2,
										overflow: "hidden",
									}}>
									<img src={editedData?.[shootStudioSampleImgUrlCol.key]} alt="Captured" style={{ width: "100%" }} />
								</Paper>
								<Box>
									<Button variant="contained" onClick={() => setCameraActive(true)}>
										Update
									</Button>
								</Box>
							</Box>
						) : (
							<CameraCapture
								currentImgUrl={editedData?.[shootStudioSampleImgUrlCol.key]}
								onCapture={(file) => {
									setImage(file);
								}}
							/>
						)}
					</Box>
				</Stack>

				<DialogActions>
					<Button variant="outlined" onClick={handleEditModalClose}>
						Cancel
					</Button>
					<Button
						autoFocus
						variant="contained"
						sx={{ mr: 1 }}
						onClick={() => handleDataSave(editedData)}
						disabled={disableEdit}>
						Save
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog fullWidth maxWidth="md" open={grnModal} onClose={handleGrnModalClose}>
				<Typography variant="h6" p={2} pb={1}>
					Upload Bulk GRN Image
				</Typography>

				<Box sx={{ px: 2, pb: 1 }}>
					<Typography variant="body2" color="text.secondary">
						The warehouse team verifies the bulk goods received against this image. Uploading it notifies the
						merchants handling these SKUs so they can align the pickup with the vendor.
					</Typography>
				</Box>

				<Box sx={{ p: 2, pt: 1, borderBottom: "1px solid #ccc" }}>
					<Typography variant="h6" fontSize={16} color={grey[700]} sx={{ width: "100%", mb: 1 }}>
						Selected Icodes ({selectedSkus.length})
					</Typography>
					<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
						{selectedSkus.map((sku) => (
							<Box
								key={sku}
								sx={{
									backgroundColor: grey[400],
									borderRadius: "5px",
									padding: "5px",
									display: "flex",
									alignItems: "center",
									gap: 1,
								}}>
								<Typography variant="subtitle1">{sku}</Typography>
							</Box>
						))}
					</Box>
				</Box>

				<Stack sx={{ p: 2 }} spacing={2}>
					<Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
						<Button variant="outlined" component="label" startIcon={<FileDownload sx={{ rotate: "180deg" }} />}>
							Choose File
							<input
								hidden
								type="file"
								accept="image/*"
								onChange={(e) => {
									const file = e.target.files?.[0];
									if (file) setGrnImage(file);
								}}
							/>
						</Button>
						{grnImage && <Typography variant="body2">{grnImage.name}</Typography>}
					</Box>

					<Typography variant="body2" color="text.secondary">
						or capture with the camera
					</Typography>

					<CameraCapture onCapture={(file) => setGrnImage(file)} />
				</Stack>

				<DialogActions>
					<Button variant="outlined" onClick={handleGrnModalClose}>
						Cancel
					</Button>
					<Button
						autoFocus
						variant="contained"
						sx={{ mr: 1 }}
						onClick={handleGrnUpload}
						disabled={disableEdit || !grnImage}>
						Upload & Notify
					</Button>
				</DialogActions>
			</Dialog>

			<SampleHistoryModal skuDetails={selectedHistorySku} modal={historyModal} setModal={setHistoryModal} />
		</Box>
	);
};

export default StudioSampleInward;
